/**
 * FiltersPanel — the left filter rail shared by every Coral-Placer view:
 * name/species search, color toggles, growth-form toggles, and a sort
 * selector. Stateless; the parent owns the FilterState.
 */
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';

import { COLOR_HEX } from './catalog';
import { ToggleChip } from './chips';
import {
  COLOR_TAGS,
  GROWTH_FORMS,
  type ColorTag,
  type FilterState,
  type GrowthForm,
  type SortKey,
} from './types';

export interface FiltersPanelProps {
  readonly filters: FilterState;
  readonly onChange: (next: FilterState) => void;
}

const SORT_LABELS: Record<SortKey, string> = {
  'name-asc': 'Name A–Z',
  'name-desc': 'Name Z–A',
  growth: 'Growth form',
  color: 'Color',
};

export function FiltersPanel({ filters, onChange }: FiltersPanelProps) {
  const toggleColor = (c: ColorTag) => {
    const has = filters.colors.includes(c);
    onChange({
      ...filters,
      colors: has ? filters.colors.filter((x) => x !== c) : [...filters.colors, c],
    });
  };
  const toggleGrowth = (g: GrowthForm) => {
    const has = filters.growthForms.includes(g);
    onChange({
      ...filters,
      growthForms: has
        ? filters.growthForms.filter((x) => x !== g)
        : [...filters.growthForms, g],
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-text-3">
          Filters
        </label>
        <Input
          type="search"
          value={filters.query}
          placeholder="Name or species…"
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
        />
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-3">
          Color
        </p>
        <div className="flex flex-wrap gap-1.5">
          {COLOR_TAGS.map((c) => (
            <ToggleChip
              key={c}
              active={filters.colors.includes(c)}
              onClick={() => toggleColor(c)}
              label={c}
              color={COLOR_HEX[c]}
            />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-3">
          Growth form
        </p>
        <div className="flex flex-wrap gap-1.5">
          {GROWTH_FORMS.map((g) => (
            <ToggleChip
              key={g}
              active={filters.growthForms.includes(g)}
              onClick={() => toggleGrowth(g)}
              label={g}
            />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-3">
          Sort by
        </p>
        <Select
          value={filters.sort}
          onChange={(e) => onChange({ ...filters, sort: e.target.value as SortKey })}
        >
          {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
            <option key={k} value={k}>
              {SORT_LABELS[k]}
            </option>
          ))}
        </Select>
      </div>

      {(filters.colors.length > 0 ||
        filters.growthForms.length > 0 ||
        filters.query !== '') && (
        <button
          type="button"
          onClick={() =>
            onChange({ ...filters, query: '', colors: [], growthForms: [] })
          }
          className="text-xs text-accent hover:underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
